import { AutocompleteTagInput, ControlledInput, InputFormContainer, Loading, Stack } from '@ui-components/neo.tax';
import { useState } from 'react';

import { PayrollExpense } from '@neo.tax/rd/hooks/usePayrollExpense';
import { RdTaxCreditContributor } from '@neo.tax/rd/hooks/useRdTaxCreditContributor';
import { useKeywordSearch } from '@neo.tax/shared';

import { LinkTicketingProviderUserToRdTaxCreditContributorModal } from '../link-ticketing-provider-user-to-rd-tax-credit-contributor-modal';
import { EditQreExpenseAllocations } from '../qre-allocation';
import { UnlinkTicketingProviderUserFromRdTaxCreditContributorModal } from '../unlink-ticketing-provider-user-from-rd-tax-credit-contributor-modal';
import { usePayrollExpenseFormAnalyticIds, usePayrollExpenseGeneralForm, useTicketingUsers } from './hooks';
import {
  getRemovedTicketingProviderUserId,
  getSourceTicketingProviderUserId,
  OptionType,
  transformTicketingProviderUserToOption,
  transformTicketingUserToOption,
} from './payroll-expense-general-form.utils';

type PayrollExpenseGeneralFormProps = {
  rdTaxCreditContributor: RdTaxCreditContributor;
  payrollExpense: PayrollExpense;
};

export const PayrollExpenseGeneralForm = ({
  payrollExpense,
  rdTaxCreditContributor,
}: PayrollExpenseGeneralFormProps): JSX.Element => {
  const { nameAnalyticId, ticketingProviderUsersAnalyticId } = usePayrollExpenseFormAnalyticIds();
  const searchKeywordState = useKeywordSearch();
  const { loading: loadingTicketingUsers, ticketingUsers } = useTicketingUsers({
    rdTaxCreditId: rdTaxCreditContributor.rdTaxCreditId,
    searchKeyword: searchKeywordState.searchKeyword,
  });
  const [ticketingProviderUserId, setTicketingProviderUserId] = useState<string>();
  const [ticketingProviderUserIdToUnlink, setTicketingProviderUserIdToUnlink] = useState<string>();

  const { control } = usePayrollExpenseGeneralForm({ payrollQre: payrollExpense.payrollQre });

  const handleChangeTicketingProviderUsers = (options: OptionType[]): void => {
    if (options.length < rdTaxCreditContributor.ticketingProviderUsers.length) {
      const removedTicketingProviderUserId = getRemovedTicketingProviderUserId(
        options,
        rdTaxCreditContributor.ticketingProviderUsers,
      );
      setTicketingProviderUserIdToUnlink(removedTicketingProviderUserId);
      return;
    }
    const sourceTicketingProviderUserId = getSourceTicketingProviderUserId(
      options,
      rdTaxCreditContributor.ticketingProviderUsers,
      ticketingUsers || [],
    );
    setTicketingProviderUserId(sourceTicketingProviderUserId);
  };

  return (
    <Stack as="form" direction="column" paddingX={4} fullHeight>
      <ControlledInput
        control={control}
        name="fullName"
        id={nameAnalyticId}
        label="Full Name / Display Name"
        rules={{ required: 'Full Name is required' }}
      />
      {!ticketingUsers ? (
        <Loading height={120} />
      ) : (
        <InputFormContainer
          inputId={ticketingProviderUsersAnalyticId}
          label="Linked Ticketing Account(s)"
          helpText="Type to find and link unmatched accounts"
        >
          <AutocompleteTagInput
            id={ticketingProviderUsersAnalyticId}
            tagProps={{ variant: 'gray', rounded: 'md', bordered: true }}
            options={(ticketingUsers || []).map(transformTicketingUserToOption)}
            values={rdTaxCreditContributor.ticketingProviderUsers.map(transformTicketingProviderUserToOption)}
            onChange={handleChangeTicketingProviderUsers}
            allowCreate={false}
            allowRemove
            setSearchKeyword={searchKeywordState.setSearchKeyword}
            isLoading={loadingTicketingUsers}
          />
        </InputFormContainer>
      )}
      <EditQreExpenseAllocations rdExpenseId={payrollExpense.payrollQre.id} />
      <LinkTicketingProviderUserToRdTaxCreditContributorModal
        isOpen={!!ticketingProviderUserId}
        onClose={(): void => setTicketingProviderUserId(undefined)}
        rdTaxCreditContributorId={rdTaxCreditContributor.id}
        ticketingProviderUserId={ticketingProviderUserId ?? ''}
      />
      <UnlinkTicketingProviderUserFromRdTaxCreditContributorModal
        isOpen={!!ticketingProviderUserIdToUnlink}
        onClose={(): void => setTicketingProviderUserIdToUnlink(undefined)}
        rdTaxCreditContributorId={rdTaxCreditContributor.id}
        ticketingProviderUserId={ticketingProviderUserIdToUnlink ?? ''}
      />
    </Stack>
  );
};
